/**
 * Where the rows come from.
 *
 * Every constraint is written in ONE form:
 *
 *     c(q) ≥ 0,   λ ≥ 0,   c · λ = 0
 *
 * so a rope, a floor and two weights closing on each other are the same kind
 * of thing and the solver has one case to handle. `c` is the room left before
 * the limit; `λ` is the force that stops it going further; and the third
 * equation — complementarity — says the force is zero unless the limit is
 * actually reached. That is the statement the old engine could not make, and
 * it is why a weight resting on the floor used to be reported as a fault.
 *
 * Four generic rules cover almost everything; a kind may add its own.
 */
import { defOf } from './registry';
import { DOWN, dot } from './vec';
import { Body, Constraint, Part, Vec2, World } from './types';
import { bodyIndex, extentAlong, isFree, partById, ropesOf, segmentsOf, spanId, spansOf, Segment } from './world';

/**
 * How short this run can get before the two parts touch.
 *
 * The subtlety is that a run is measured between CONTACT POINTS while a part's
 * extent is measured from its CENTRE, and the two differ by exactly where the
 * rope meets it: a weight is held by the hook on its top face, a sheave is met
 * at a rim beside its axle. Centres must stay `extentA + extentB` apart, so
 *
 *     L ≥ extentA + extentB + (contact offset at b) − (contact offset at a)
 *
 * along the run. Ignoring those offsets made every weight hanging a short rope
 * below its own sheave start out already overlapping it — the arrangement the
 * bench opens on.
 */
export function minRunLength(w: World, seg: Segment, pa: Part, pb: Part): number {
    const oa = dot({ x: seg.pa.x - pa.at.x, y: seg.pa.y - pa.at.y }, seg.dir);
    const ob = dot({ x: seg.pb.x - pb.at.x, y: seg.pb.y - pb.at.y }, seg.dir);
    return extentAlong(w, pa, seg.dir) + extentAlong(w, pb, seg.dir) + ob - oa;
}

/**
 * ∂(length of this segment)/∂(each coordinate).
 *
 * A segment's length is |b − a|, so moving b along û lengthens it by one and
 * moving a along û shortens it by one. Projected onto each body's axes, that
 * is the row. It works at any angle, which is what lets a rope run over an
 * edge sheave to a block on a table without anything new.
 */
function lengthRows(seg: Segment, byPart: Map<string, Body>): Constraint['rows'] {
    const rows: Constraint['rows'] = [];
    const push = (partId: string, sign: number): void => {
        const body = byPart.get(partId);
        if (!body || !isFree(body)) return;
        body.axes.forEach((axis, i) => {
            const c = sign * dot(seg.dir, axis);
            if (Math.abs(c) > 1e-12) rows.push({ coord: { bodyId: body.id, axis: i }, coefficient: c });
        });
    };
    push(seg.b.partId, +1);
    push(seg.a.partId, -1);
    return rows;
}

/** Add up coefficients that land on the same coordinate. */
function merge(rows: Constraint['rows']): Constraint['rows'] {
    const m = new Map<string, { coord: Constraint['rows'][0]['coord']; coefficient: number }>();
    rows.forEach(r => {
        const k = `${r.coord.bodyId}#${r.coord.axis}`;
        const at = m.get(k);
        if (at) at.coefficient += r.coefficient;
        else m.set(k, { coord: r.coord, coefficient: r.coefficient });
    });
    return [...m.values()].filter(r => Math.abs(r.coefficient) > 1e-12);
}

export function constraintsOf(w: World, bodies: Body[]): Constraint[] {
    const byPart = bodyIndex(bodies);
    const segs = segmentsOf(w);
    const out: Constraint[] = [];

    /* ── a span of rope cannot be stretched, but may hang slack ───────────
     *
     * One row per SPAN, not per rope. A span is the stretch along which the
     * tension cannot vary, so its multiplier IS that tension — and where the
     * rope divides is a question asked of the parts it runs over rather than
     * assumed. While every sheave is ideal each rope is one span and this is
     * exactly the row it always was.
     *
     * The length includes what is wrapped on each wheel, because that is rope
     * too. Its DERIVATIVE is not in the rows, and for good reason: where every
     * run is square the wrap is exactly half a turn and never changes, so the
     * term is constant and contributes nothing. Where runs turn as they move it
     * does change — and such an arrangement already reports `exact: false`,
     * which is the engine saying this same thing. */
    const line = new Map(ropesOf(w).map(r => [r.id, r]));
    spansOf(w).forEach(span => {
        const rigid = !!line.get(span.ropeId)?.rigid;
        out.push({
            id: span.id,
            kind: 'rope',
            // c = L₀ − L. Positive means there is slack left.
            rows: merge(span.segments.flatMap(s => lengthRows(s, byPart)).map(r => ({ ...r, coefficient: -r.coefficient }))),
            gap: span.was - span.now,
            /* A rod is this same row with the inequality taken off. Its
             * multiplier may then come out negative, which is the rod in
             * compression — the one number a bolt can never give a reader. */
            oneSided: !rigid,
            reports: 'tension',
            sourceId: span.ropeId,
            label: line.get(span.ropeId)?.label,
        });
    });

    /* ── two parts on one run cannot pass through each other ─────────────
     *
     * One rule, and it covers both "a weight cannot be hauled up through its
     * own sheave" and "two weights on one rope cannot meet" — the first of
     * which used to be a clamp in the drawing code and the second of which was
     * simply not modelled. */
    segs.forEach(seg => {
        const pa = partById(w, seg.a.partId), pb = partById(w, seg.b.partId);
        if (!pa || !pb) return;
        const ba = byPart.get(pa.id), bb = byPart.get(pb.id);
        // Parts bolted together travel as one and cannot close on each other.
        if (ba && bb && ba.id === bb.id) return;
        const minGap = minRunLength(w, seg, pa, pb);
        out.push({
            id: `sep:${seg.ropeId}:${seg.index}`,
            kind: 'separation',
            // c = L − minGap. Positive means they are still clear of each other.
            rows: merge(lengthRows(seg, byPart)),
            gap: seg.length - minGap,
            oneSided: true,
            reports: 'normal',
            sourceId: seg.ropeId,
            // Named, because "two parts on one rope meet" is a sentence about
            // the model and "m₁ reaches the pulley" is one about the bench.
            label: `${defOf(pa).name(pa as never, w)} and ${defOf(pb).name(pb as never, w)}`,
        });
    });

    // ── the room: the floor pushes up, the ceiling stops things rising ───
    bodies.filter(isFree).forEach(body => {
        body.partIds.forEach(id => {
            const part = partById(w, id);
            if (!part) return;
            const reach = extentAlong(w, part, DOWN);
            /* Where it actually stops, off its outline when it has one. A
             * table's `at` is the middle of its BASE, so a reach measured both
             * ways from `at` put its bottom a whole height under the floor. */
            const shape = defOf(part).outline?.(part as never, w);
            const bottom = shape?.length ? Math.min(...shape.map(v => v.y)) : part.at.y - reach;
            const top = shape?.length ? Math.max(...shape.map(v => v.y)) : part.at.y + reach;
            const down = (axis: Vec2): number => -axis.y;
            out.push({
                id: `floor:${id}`,
                kind: 'contact',
                // c = (bottom of the part) − floor.
                rows: body.axes.map((axis, i) => ({ coord: { bodyId: body.id, axis: i }, coefficient: -down(axis) }))
                    .filter(r => Math.abs(r.coefficient) > 1e-12),
                gap: bottom - w.room.floor,
                /* A part that stands on the floor and STAYS on it — a table, a
                 * wedge let slide — is held to it both ways. It would otherwise
                 * be free to lift, and a block hitting the floor at a wedge's
                 * foot, still held to the face (see `kinds/surface.ts`), yanked
                 * the wedge up off the floor and the run could not go on. */
                oneSided: !defOf(part).onFloor,
                reports: 'normal',
                sourceId: id,
                label: 'the floor',
            });
            /* The floor's grip, capped by the floor's own push — the same
             * coupling a table's face uses, so a block on a rough floor and a
             * block on a rough table are one mechanism. Sideways only: along
             * the floor is the one direction friction acts in. How rough is
             * the part's to say — see `PartKind.floorGrip`. */
            const grip = defOf(part).floorGrip?.(part as never) ?? 0;
            if (grip === 'fixed' || grip > 0) {
                out.push({
                    id: `floorgrip:${id}`,
                    kind: 'friction',
                    rows: body.axes.map((axis, i) => ({ coord: { bodyId: body.id, axis: i }, coefficient: axis.x }))
                        .filter(r => Math.abs(r.coefficient) > 1e-12),
                    gap: 0,
                    oneSided: false,
                    coupledTo: { constraintId: `floor:${id}`, factor: grip === 'fixed' ? Infinity : grip },
                    reports: 'friction',
                    sourceId: id,
                    label: 'the floor',
                });
            }
            out.push({
                id: `ceiling:${id}`,
                kind: 'contact',
                // c = ceiling − (top of the part).
                rows: body.axes.map((axis, i) => ({ coord: { bodyId: body.id, axis: i }, coefficient: down(axis) }))
                    .filter(r => Math.abs(r.coefficient) > 1e-12),
                gap: w.room.ceiling - top,
                oneSided: true,
                reports: 'normal',
                sourceId: id,
                label: 'the ceiling',
            });
        });
    });

    // ── whatever a kind adds for itself ──────────────────────────────────
    w.parts.forEach(part => {
        const extra = defOf(part).constraints?.(part as never, w, bodies);
        if (extra) out.push(...extra);
    });

    return out.filter(c => c.rows.length > 0);
}

/**
 * Which way a constraint pushes a body, per unit of its multiplier.
 *
 * THE one answer to that question. It was four answers in three shapes — the
 * statics had one, the free-body builder another, the per-part builder a copy
 * of that — and when the first was fixed the other two kept the bug, which is
 * how a rope and a separation acting along the same slanted run came out
 * pointing in different directions on the diagram and in the arithmetic.
 *
 * The subtlety it exists to hold in one place: `Constraint.rows` carries only
 * the PROJECTION onto each body's own axes, and a hanging body has one axis,
 * straight down. Anything acting along a run at an angle therefore loses its
 * sideways half on the way in. So a rope or a separation takes its direction
 * from the RUN, which is where it actually acts, and only contacts and friction
 * — whose direction is a body axis by construction — may be read off the rows.
 */
export function forceLine(w: World, c: Constraint, body: Body, byPart: Map<string, Body>): Vec2 {
    if (c.kind === 'rope' || c.kind === 'separation') {
        // A rope pulls its ends together; a separation pushes them apart. One
        // sign is the whole difference between them.
        const sign = c.kind === 'rope' ? 1 : -1;
        let v: Vec2 = { x: 0, y: 0 };
        segmentsOf(w).forEach(g => {
            const mine = c.kind === 'rope' ? spanId(g.ropeId, g.span) : `sep:${g.ropeId}:${g.index}`;
            if (mine !== c.id) return;
            if (byPart.get(g.a.partId)?.id === body.id) v = { x: v.x + g.dir.x * sign, y: v.y + g.dir.y * sign };
            if (byPart.get(g.b.partId)?.id === body.id) v = { x: v.x - g.dir.x * sign, y: v.y - g.dir.y * sign };
        });
        return v;
    }
    let f: Vec2 = { x: 0, y: 0 };
    c.rows.forEach(r => {
        if (r.coord.bodyId !== body.id) return;
        const axis = body.axes[r.coord.axis];
        if (axis) f = { x: f.x + axis.x * r.coefficient, y: f.y + axis.y * r.coefficient };
    });
    return f;
}
